import { useState } from 'react';
import { TouchableOpacity } from 'react-native';

import { type FileLocation, uploadFile } from '@gitbook/app-database';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Button,
    EmojiPicker,
    Image,
    Paper,
    Popover,
    PopoverRoot,
    PopoverTrigger,
    Tab,
    Tabs,
    toast,
    usePopoverClose,
} from '@gitbook/spine-react';
import { useStateWithDeps } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';
import { isUrl } from '@gitbook/util-url';

import { getImageResizedURL, promptFile } from '../../utils';
import { ControlledContentIcon } from './ContentIcon';

type ContentEditableIconProps = {
    emoji: string | undefined;
    iconURL: string | undefined;
    fallbackText: string;
    uploadTo: FileLocation;
    onClear: () => void;
    size: number;
    onEmojiChange: (emoji: string) => void;
    onIconURLChange: (iconURL: string) => void;
    className?: string;
    withBackground?: boolean;
    style?: React.CSSProperties;
    itemStyle?: React.CSSProperties;
};

/**
 * Editable icon for content.
 */
export function ContentEditableIcon(props: ContentEditableIconProps) {
    const { emoji, iconURL, uploadTo, onClear, onEmojiChange, onIconURLChange, ...iconProps } =
        props;

    return (
        <PopoverRoot>
            <PopoverTrigger>
                <ControlledContentIcon iconURL={iconURL} emoji={emoji} {...iconProps} />
            </PopoverTrigger>
            <Popover placement="bottom-start">
                <PopoverContent {...props} />
            </Popover>
        </PopoverRoot>
    );
}

function PopoverContent(props: ContentEditableIconProps) {
    const { emoji, iconURL, uploadTo, onClear, onEmojiChange, onIconURLChange } = props;
    const [tab, setTab] = useStateWithDeps<'emoji' | 'image'>(iconURL ? 'image' : 'emoji', [
        iconURL,
    ]);
    const showClearButton = !!emoji || !!iconURL;
    const closePopover = usePopoverClose();
    const [error, setError] = useState<string | undefined>(undefined);

    /**
     * Handle clear icon and emoji prompt. Clears icon and published emoji.
     */
    const onPressClear = () => {
        onClear();
        closePopover();
    };

    /**
     * Handle an emoji being selected.
     */
    const onPickEmoji = (emoji: string) => {
        onEmojiChange(emoji);
        closePopover();
    };

    /**
     * Handle a file being uploaded.
     */
    const onPressUpload = async () => {
        const [file] = await promptFile({ accept: 'images' });
        if (!file) {
            return;
        }

        const errorMessage = await validateIconFile(file);
        if (errorMessage) {
            setError(errorMessage);
            return;
        }

        const promise = uploadFile(uploadTo, file);
        toast.promise(promise, {
            loading: 'Uploading icon…',
            success: 'Icon uploaded',
            error: 'Error while uploading your files',
        });

        const metadata = await promise;

        if (metadata && isUrl(metadata.downloadURL)) {
            onIconURLChange(metadata.downloadURL);
        }

        closePopover();
    };
    return (
        <Paper>
            <div className="flex items-center gap-4 border-b px-3 pt-1">
                <Tabs>
                    <Tab active={tab === 'emoji'} onPress={() => setTab('emoji')} label="Emojis" />
                    <Tab active={tab === 'image'} onPress={() => setTab('image')} label="Image" />
                </Tabs>
                {showClearButton && (
                    <Button onClick={onPressClear} label="Remove" size="small" kind="blank" />
                )}
            </div>
            {(() => {
                switch (tab) {
                    case 'emoji':
                        return <EmojiPicker onPick={onPickEmoji} />;

                    case 'image':
                        return (
                            <div className="flex flex-1 gap-4 p-4" style={{ maxWidth: 400 }}>
                                <TouchableOpacity onPress={onPressUpload}>
                                    <IconPreview {...props} />
                                </TouchableOpacity>
                                <div className="flex flex-1 flex-col gap-2">
                                    <Button
                                        kind="secondary"
                                        onClick={onPressUpload}
                                        label="Upload an image"
                                    />
                                    <p className="text-muted">
                                        Use a square image, at least 32×32px (180×180px
                                        recommended).
                                    </p>
                                    {error ? (
                                        <Alert kind="danger" size="xsmall" details={error} />
                                    ) : null}
                                </div>
                            </div>
                        );
                    default:
                        assertNever(tab);
                }
            })()}
        </Paper>
    );
}

async function validateIconFile(file: File): Promise<string | undefined> {
    try {
        const dimensions = await getImageDimensions(file);
        if (dimensions.width !== dimensions.height) {
            return 'The image must be square.';
        }

        const minSize = 32;
        if (dimensions.width < minSize || dimensions.height < minSize) {
            return `The image must be at least ${minSize}×${minSize}px.`;
        }
    } catch {
        return 'Invalid image file.';
    }
}

async function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const img = new window.Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
            resolve({ width: img.width, height: img.height });
            URL.revokeObjectURL(url);
        };

        img.onerror = reject;
        img.src = url;
    });
}

function IconPreview(props: { iconURL?: string }) {
    return (
        <div
            className="flex items-center justify-center bg-muted p-3"
            style={{ width: 120, height: 120 }}
        >
            {props.iconURL ? (
                <Image
                    src={getImageResizedURL(props.iconURL, {
                        width: 120 - 24,
                        height: 120 - 24,
                        fit: 'contain',
                    })}
                    className="max-h-full w-full flex-1 object-contain"
                />
            ) : (
                <Icon.Image size={18} className="text-muted" />
            )}
        </div>
    );
}
